export interface LumisialConJerarquia {
  nombre: string;
  diocesis?: { nombre: string; tipo: 'diocesis' | 'prefectura' } | null;
  provincia_eclesiastica?: { nombre: string } | null;
}

/**
 * Arma el subtítulo de jerarquía de un lumisial: "L. {nombre} · D./P. {nombre} · P.E. {nombre}".
 * Formato adoptado del PR #170 (ya en `main`) al reconciliar con este cambio: cada nivel lleva
 * su prefijo (L. lumisial, D. diócesis o P. prefectura según `diocesis.tipo`, P.E. provincia
 * eclesiástica). Devuelve '' si no hay lumisial.
 *
 * Acepta cualquier forma estructural con estos campos (el `Lumisial` completo de `src/lib/supabase.ts`,
 * o la forma parcial que trae `SolicitudRolWithDetails.miembro.lumisial`), sin exigir el tipo `Lumisial` entero.
 */
export function formatJerarquiaMiembro(lumisial?: LumisialConJerarquia | null): string {
  if (!lumisial) return '';

  const partes: string[] = [`L. ${lumisial.nombre}`];

  if (lumisial.diocesis) {
    const prefijoDiocesis = lumisial.diocesis.tipo === 'prefectura' ? 'P.' : 'D.';
    partes.push(`${prefijoDiocesis} ${lumisial.diocesis.nombre}`);
  }

  if (lumisial.provincia_eclesiastica) {
    partes.push(`P.E. ${lumisial.provincia_eclesiastica.nombre}`);
  }

  return partes.join(' · ');
}

export interface JerarquiaIds {
  provincia_eclesiastica_id?: string | null;
  diocesis_id?: string | null;
  lumisial_id?: string | null;
}

/**
 * Compara jerarquía solicitada/editada vs. jerarquía real del miembro, nivel por nivel.
 * Un nivel solo se compara si está presente en ambos lados (el scope del rol determina
 * qué niveles aplican); si falta en alguno de los dos, se omite esa comparación.
 */
export function difiereJerarquia(solicitado: JerarquiaIds, real: JerarquiaIds): boolean {
  const difiereNivel = (a?: string | null, b?: string | null): boolean => {
    if (!a || !b) return false;
    return a !== b;
  };

  return (
    difiereNivel(solicitado.provincia_eclesiastica_id, real.provincia_eclesiastica_id) ||
    difiereNivel(solicitado.diocesis_id, real.diocesis_id) ||
    difiereNivel(solicitado.lumisial_id, real.lumisial_id)
  );
}
